import { EMAIL_SUBSCRIBE, EMAIL_UNSUBSCRIBE, SMS_RECEIVED } from 'constants/esDataTypes';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import { Query } from 'modules/journey/components/FilterInputsList/FilterInputsList.types';
import { IS_NOT_SET, IS_SET } from 'utils/comparatorTypes';
import { EVENT_TYPES } from 'utils/dataTypes';
import { DATE } from 'utils/fieldTypes';
import { isRelativeESDate } from 'utils/relativeDateTime';

import { validateRelativeDate, isEsDateString } from 'utils/validateDate';
import { BackendJourneyConversion } from 'modules/workflows/types/workflow';
import { RULE_TYPES } from '../constants';
import {
  ChildRule,
  ChildRuleValidationErrors,
  ContainsCloningError,
  ErrorObjectType,
  EventAndSearchQueryValidationErrors,
  Rule,
  RuleValidationErrors,
  Rules,
  SearchQueriesValidationErrors,
  SearchQueryValidationErrors,
  ServerReportedCloningError,
  ValidationErrors,
} from '../types';
import { State } from './useRuleBuilder';

export const hasValidationError = (errorObject?: ErrorObjectType) => {
  return errorObject?.validationError || false;
};
const generateErrorObject = (value = '') => {
  return { validationError: true, displayValue: value };
};

const ALL_ERROR = {
  field: generateErrorObject(),
  value: generateErrorObject(),
  comparatorType: generateErrorObject(),
};

/** Returns true if there is an error */
export const validateDateSearchQuery = (value: string) => {
  if (isEsDateString(value)) return false;
  if (value.includes('{{') || isRelativeESDate(value)) return validateRelativeDate(value);
  return true;
};

const validateSearchQuery = (searchQuery: Partial<Query>): SearchQueryValidationErrors => {
  const { field, fieldType, value, comparatorType } = searchQuery;
  const hasSetComparator = comparatorType === IS_SET || comparatorType === IS_NOT_SET;
  const searchQueryErrorObject = {
    ...(!field && { field: generateErrorObject() }),
    ...(!hasSetComparator && !value && { value: generateErrorObject() }),
    ...(!hasSetComparator &&
      fieldType === DATE &&
      typeof value === 'string' &&
      validateDateSearchQuery(value) && { value: generateErrorObject() }),
    ...(!comparatorType && { comparatorType: generateErrorObject() }),
  };
  return isEmpty(searchQueryErrorObject) ? {} : searchQueryErrorObject;
};

const reduceSearchQueryErrors = (
  acc: SearchQueriesValidationErrors,
  searchQuery: Partial<Query>,
  idx: number,
) => {
  const searchQueryValidations = validateSearchQuery(searchQuery);
  if (!isEmpty(searchQueryValidations)) {
    acc[idx] = searchQueryValidations;
  }
  return acc;
};

const validateChildRule = (childRule: ChildRule) => {
  const childRuleErrorObject = {} as EventAndSearchQueryValidationErrors;
  const isPropertyRule = childRule?.ruleType === RULE_TYPES.USER_PROPERTY;

  if (isPropertyRule && isEmpty(childRule?.searchQuery?.searchQueries))
    return { searchQuery: { searchQueries: { 0: ALL_ERROR } } };

  if (!isPropertyRule && !childRule?.eventName) {
    childRuleErrorObject.eventName = generateErrorObject();
  }

  const searchQueriesErrors = childRule?.searchQuery?.searchQueries?.reduce(
    reduceSearchQueryErrors,
    {},
  );

  if (!isEmpty(searchQueriesErrors)) {
    childRuleErrorObject.searchQuery = { searchQueries: searchQueriesErrors };
  }

  return isEmpty(childRuleErrorObject) ? {} : childRuleErrorObject;
};

const reduceChildRuleErrors = (
  acc: ChildRuleValidationErrors,
  childRule: ChildRule,
  idx: number,
) => {
  const childRuleValidations = validateChildRule(childRule);
  if (!isEmpty(childRuleValidations)) {
    acc[idx] = childRuleValidations;
  }
  return acc;
};

const validateRule = (rule: Rule) => {
  const childRulesErrors = rule.childRules.reduce(reduceChildRuleErrors, {});
  return childRulesErrors;
};
const reduceRulesErrors = (acc: RuleValidationErrors, rule: Rule, idx: number) => {
  const ruleValidations = validateRule(rule);
  if (!isEmpty(ruleValidations)) {
    acc[idx] = ruleValidations;
  }
  return acc;
};

export const validateRules = (rules: Rules): ValidationErrors => {
  const rulesErrors = rules.reduce(reduceRulesErrors, {});
  return isEmpty(rulesErrors) ? {} : { rules: rulesErrors };
};

function lowercaseFirstLetter(text: string) {
  return text[0].toLowerCase() + text.slice(1);
}

const getCorrectDataType = (dataType: string | undefined) => {
  // warning: we do this because the backend returns dataTypes with the GraphQL mappings instead of the JSON mappings. Start node uses the JSON mappings
  switch (dataType) {
    case 'Subscribe':
      return EMAIL_SUBSCRIBE;
    case 'UnSubscribe':
      return EMAIL_UNSUBSCRIBE;
    case 'SMSReceived':
      return SMS_RECEIVED;
    default:
      return dataType ? lowercaseFirstLetter(dataType) : dataType;
  }
};

// this function applies across domains
export const transformSearchQueries = (childRule: ChildRule & ContainsCloningError) => {
  const { searchQueries } = childRule?.searchQuery;
  if (!searchQueries) return childRule;
  const transformedSearchQueries = cloneDeep(searchQueries);
  const newSearchQueries = transformedSearchQueries.map((query: Partial<Query>) => {
    // cloneDeep ensures this is a brand new object

    query.dataType = getCorrectDataType(query.dataType);
    return {
      ...query,
      containsCloningError: childRule?.containsCloningError,
    };
  });

  const newChildRule = cloneDeep(childRule);
  newChildRule.searchQuery.searchQueries = newSearchQueries;
  return newChildRule;
};

export const transformServerReportedCloningErrors = (
  errors: ServerReportedCloningError[],
  rules: Rules,
): ValidationErrors => {
  // all the 0's are because we currently only support one rule and one child rule.
  const holdEditorData = rules?.[0]?.childRules?.[0];
  const holdEditorSearchQueries = holdEditorData?.searchQuery?.searchQueries ?? [];
  const searchQueriesErrors = holdEditorSearchQueries?.reduce(
    (acc: SearchQueriesValidationErrors, searchQuery, idx) => {
      const matchingServerError = errors?.find((error) => {
        return (
          error?.fieldName === searchQuery?.field &&
          error?.prevCombinator === searchQuery?.comparatorType &&
          error?.prevValue === searchQuery?.value
        );
      });
      if (matchingServerError) {
        acc[idx] = {
          value: generateErrorObject(matchingServerError.prevValue),
        };
      }

      return acc;
    },
    {},
  );

  const hasCustomEventError =
    errors?.[0]?.fieldName === EVENT_TYPES.CUSTOM_EVENT &&
    holdEditorData.eventName === errors?.[0]?.prevValue;
  // 0 here represents the rule index, which is always 0, same with child rule index
  return {
    rules: {
      0: {
        0: {
          // all custom events are currently invalid for hold tile
          ...(hasCustomEventError && { eventName: generateErrorObject(errors?.[0].prevValue) }),
          searchQuery: { searchQueries: searchQueriesErrors },
        },
      },
    },
  };
};

export const getJourneyConversionRuleIdx = (
  journeyConversion: BackendJourneyConversion | undefined,
  state: State,
): number => {
  let idx = -1;
  if (journeyConversion && !isEmpty(state.rules)) {
    for (let i = 0; i < state.rules.length; i++) {
      if (
        !isEmpty(state.rules[i].childRules) &&
        state.rules[i].childRules[0].journeyConversionRuleMetadata?.journeyConversionId ===
          journeyConversion.id
      ) {
        idx = i;
        break;
      }
    }
  }
  return idx;
};
